
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface ConvertLeadToClientParams {
  leadId: string;
  conversionNotes?: string;
}

interface ConversionResult {
  success: boolean;
  client_id?: string;
  lead_id?: string;
  client_name?: string;
  conversion_date?: string;
  error?: string;
  error_code?: string;
}

export const useLeadToClientConversion = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  const convertLeadToClientMutation = useMutation({
    mutationFn: async ({ leadId, conversionNotes }: ConvertLeadToClientParams): Promise<ConversionResult> => {
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase.rpc('convert_lead_to_client', {
        p_lead_id: leadId,
        p_conversion_notes: conversionNotes || null
      });

      if (error) {
        console.error('Error converting lead to client:', error);
        throw error;
      }

      // Safely cast the data with proper type checking
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response from conversion function');
      }

      return data as unknown as ConversionResult;
    },
    onSuccess: (result) => {
      if (result.success) {
        // Invalidate relevant queries
        queryClient.invalidateQueries({ queryKey: ['leads'] });
        queryClient.invalidateQueries({ queryKey: ['clients'] });
        queryClient.invalidateQueries({ queryKey: ['lead', result.lead_id] });
        
        toast.success(`Lead "${result.client_name}" successfully converted to client!`);
      } else {
        toast.error(`Conversion failed: ${result.error}`);
      }
    },
    onError: (error) => {
      console.error('Lead conversion error:', error);
      toast.error('Failed to convert lead to client. Please try again.');
    },
  });

  return {
    convertLeadToClient: convertLeadToClientMutation.mutate,
    isConverting: convertLeadToClientMutation.isPending,
    conversionResult: convertLeadToClientMutation.data,
  };
};
